import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'

import {
  ApiError,
  api,
  can,
  refusalReason,
  codeWords,
  shortDate,
  type ApprovalTask,
  type ClearanceStatus,
  type IssuedProcurementCredential,
  type ProcurementCredential,
  type ProcurementRequest,
  type StatutoryCheck,
} from '../lib/api'
import { ErrorBox, ListCount, Loading } from '../components/DataTable'
import { Dialog, KpiTile, Status, TaskList, Toggletip, type TaskState, type Tone } from '../components/kit'
import { SOURCE_SYSTEMS } from '../components/integrations/catalogue'
import { scopesSentence } from '../lib/registry'
import { useSave } from '../lib/save'
import '../styles/integrations.css'

// Purchase requests, as procurement sees them.
//
// # WHAT THIS SCREEN IS FOR
//
// A buyer never has to open this product to know whether they may buy: Oracle,
// SAP or Ariba already tells them. This is where the third party risk team sees
// what the buying systems asked, what each supplier was told and why, and where
// an administrator hands a buying system the key it files requests with.
//
// # WHY THREE TILES AND A SHORT REASON
//
// The three answers are the first thing anyone wants: how many suppliers may be
// bought from, how many are waiting, how many are stopped. Each tile opens the
// list it counts. Under that, a row names the reason in a few words ("Tier not
// decided") rather than the sentence the buying system was sent, because
// "Awaiting assessment" is three different pieces of work and the short
// reason says which. The full sentences, who has to act on each, and the
// conditions are in the drawer a row opens.

const STATUSES: ClearanceStatus[] = ['CLEARED', 'PENDING_ASSESSMENT', 'BLOCKED']

// The API's default page of purchase requests (repo.ProcurementQueueTx).
const QUEUE_PAGE = 200

// The tile says it in full; the row label stays at two words.
const TILE_WORD: Record<ClearanceStatus, string> = {
  CLEARED: 'Cleared',
  PENDING_ASSESSMENT: 'Awaiting assessment',
  BLOCKED: 'Blocked',
}
const STATUS_WORD: Record<ClearanceStatus, { word: string; tone: Tone }> = {
  CLEARED: { word: 'Cleared', tone: 'ok' },
  PENDING_ASSESSMENT: { word: 'Awaiting assessment', tone: 'warn' },
  BLOCKED: { word: 'Blocked', tone: 'crit' },
}

// The reason codes from packages/core/domain/clearance.go, each as a few words,
// whether it stops a purchase outright, and who has to act on it. The codes are
// stable because a buying system's own workflow may be keyed on them.
const REASON: Record<string, { short: string; blocks?: boolean; state?: TaskState; owner?: string; note?: string }> = {
  SUPPLIER_REJECTED: { short: 'Supplier rejected', blocks: true },
  SUPPLIER_OFFBOARDED: { short: 'Supplier offboarded', blocks: true },
  SUPPLIER_LEAVING: { short: 'Being offboarded', blocks: true },
  SUPPLIER_SUSPENDED: { short: 'Supplier suspended', blocks: true },
  CRITICAL_FINDING: { short: 'Critical finding', blocks: true },
  REVIEW_LAPSED: { short: 'Review lapsed', state: 'missing', owner: 'Risk team' },
  TIER_NOT_DECIDED: { short: 'Tier not decided', state: 'missing', owner: 'Risk team' },
  TIER_NEEDS_ASSESSMENT: { short: 'Assessment under way', state: 'progress', owner: 'Risk team', note: 'Started' },
  STATUTORY_INCOMPLETE: { short: 'Statutory identity incomplete', state: 'missing', owner: 'Finance or supplier' },
  APPROVED: { short: 'Approved in the register', state: 'done' },
  LOW_RISK_COMPLETE: { short: 'Low risk, identity complete', state: 'done' },
}

function reasonShort(code: string, words: string): string {
  const known = REASON[code]
  if (known) return known.short
  return words.length <= 40 ? words.replace(/\.$/, '') : words.slice(0, 38).replace(/\s+\S*$/, '') + '…'
}

const MATCHED_WORD: Record<string, string> = {
  CREATED: 'New vendor',
  SUPPLIER_LINK: 'Known supplier',
  PAN: 'Matched by PAN',
  GSTIN: 'Matched by GSTIN',
}

const APPROVAL_WORD: Record<ApprovalTask['state'], { word: string; tone: Tone }> = {
  WAITING: { word: 'Waiting', tone: 'info' },
  APPROVED: { word: 'Approved', tone: 'ok' },
  DENIED: { word: 'Denied', tone: 'crit' },
  UNMATCHED: { word: 'Left for a person', tone: 'warn' },
  FAILED: { word: 'Not answered', tone: 'warn' },
}

// The buying systems by the names their owners use, with the mark the
// integrations catalogue gives each. A request filed with a key rather than a
// connector names whatever buying system it likes, and reads as words. (Not
// systemName: that one never turns a key into words, because an import's key
// names the tool the customer is leaving.)
function buyingSystem(code: string): { name: string; mark: string; color: string } {
  const known = SOURCE_SYSTEMS[code.toUpperCase()]
  if (known) return known
  return { name: codeWords(code) || 'Your system', mark: 'API', color: '#3d4a61' }
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues.length > 0) return err.issues.map((i) => i.detail).join(' ')
    return err.message
  }
  return err instanceof Error ? err.message : String(err)
}

// A toggletip holds at most 200 characters.
function clip(text: string, limit = 200): string {
  if (text.length <= limit) return text
  return text.slice(0, limit - 2).replace(/\s+\S*$/, '') + '…'
}

export function ClearanceChip({ status }: { status: ClearanceStatus }) {
  const s = STATUS_WORD[status]
  return <Status tone={s.tone}>{s.word}</Status>
}

// The filter is in the address, so a tile is a link and the list it opens can
// be sent to somebody.
function statusFromHash(): ClearanceStatus | '' {
  const q = window.location.hash.split('?')[1] ?? ''
  const s = new URLSearchParams(q).get('status')
  return s && (STATUSES as string[]).includes(s) ? (s as ClearanceStatus) : ''
}

function SourceMark({ code }: { code: string }) {
  const s = buyingSystem(code)
  return (
    <span className="int-source">
      <span className="int-mark int-mark-sm" style={{ background: s.color }} aria-hidden="true">
        {s.mark}
      </span>
      {s.name}
    </span>
  )
}

export function Procurement() {
  const [filter, setFilter] = useState<ClearanceStatus | ''>(statusFromHash)
  const [rows, setRows] = useState<ProcurementRequest[] | null>(null)
  const [counts, setCounts] = useState<Partial<Record<ClearanceStatus, number>>>({})
  // How many requests the counts were made from. The API counts the page it
  // returns, at most 200 rows, so past that the tiles say what they cover.
  const [counted, setCounted] = useState(0)
  const [error, setError] = useState<unknown>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onHash = () => setFilter(statusFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const load = useCallback(() => {
    api
      .procurementRequests(filter || undefined)
      .then((r) => {
        setRows(r.requests)
        // The counts are always the whole picture, so a filtered read does not
        // replace them with the filtered list's own.
        if (!filter) {
          setCounts(r.counts)
          setCounted(r.requests.length)
        }
        setError(null)
      })
      .catch(setError)
  }, [filter])
  useEffect(load, [load])

  // Arriving on a filtered address, the tiles still need the unfiltered
  // counts, which only the unfiltered read carries.
  useEffect(() => {
    if (!filter) return
    api
      .procurementRequests()
      .then((r) => {
        setCounts(r.counts)
        setCounted(r.requests.length)
      })
      .catch(() => undefined)
  }, [filter])

  const total = STATUSES.reduce((n, s) => n + (counts[s] ?? 0), 0)
  const selected = rows?.find((r) => r.id === openId) ?? null

  return (
    <div className="p1-page int-proc">
      <div className="p1-head">
        <div>
          <h1>Purchase requests</h1>
          <p className="muted">What buying systems asked, and what each supplier was told</p>
        </div>
      </div>

      <div className="int-tiles" role="group" aria-label="Answers">
        {STATUSES.map((s) => (
          <div key={s} className="int-tile" data-on={filter === s}>
            <KpiTile
              value={counts[s] ?? 0}
              label={TILE_WORD[s]}
              href={'#/procurement?status=' + s}
              hot={s === 'BLOCKED' && (counts[s] ?? 0) > 0}
            />
          </div>
        ))}
      </div>

      {counted >= QUEUE_PAGE && <p className="int-away">Counted from the latest {QUEUE_PAGE} requests</p>}

      {error != null && <ErrorBox error={error} what="Purchase requests" />}
      {rows == null && error == null && <Loading what="purchase requests" />}

      {rows && rows.length === 0 && (
        <div className="empty">
          <strong>{filter ? 'No requests with this answer' : 'No buying system has sent a purchase request yet'}</strong>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {filter ? (
              <a href="#/procurement">Show every request</a>
            ) : (
              <>
                Connect one in <a href="#/settings?t=integrations">Settings, Integrations</a>, or issue a key below
              </>
            )}
          </p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="panel table-wrap">
          <table className="table int-req-table">
            <caption className="visually-hidden">Purchase requests and the clearance of the supplier each one names</caption>
            <thead>
              <tr>
                <th scope="col">Supplier</th>
                <th scope="col">Asked by</th>
                <th scope="col">Answer</th>
                <th scope="col">Why</th>
                <th scope="col">Last asked</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const first = r.clearance.reasons[0]
                const more = r.clearance.reasons.length - 1
                return (
                  <tr
                    key={r.id}
                    className="int-row"
                    onClick={() => {
                      setOpenId(r.id)
                      setOpen(true)
                    }}
                  >
                    <td>
                      {/* The name is the button, so a keyboard reaches the
                          drawer the same way a click on the row does. */}
                      <button
                        type="button"
                        className="int-linkbtn"
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenId(r.id)
                          setOpen(true)
                        }}
                      >
                        {r.clearance.legalName}
                      </button>
                      <span className="int-sub">
                        <span className="kit-mono">{r.clearance.vendorCode}</span>
                        {r.clearance.tierLabel ? ', ' + r.clearance.tierLabel : ''}
                      </span>
                    </td>
                    <td>
                      <SourceMark code={r.sourceSystem} />
                      <span className="int-sub kit-mono">{r.externalRequestId}</span>
                    </td>
                    <td>
                      <ClearanceChip status={r.clearance.status} />
                    </td>
                    <td>
                      {first ? reasonShort(first.code, first.words) : <span className="muted">No reason recorded</span>}
                      {more > 0 && <span className="int-sub">and {more} more</span>}
                      {r.clearance.conditions.length > 0 && (
                        <span className="int-sub">
                          {r.clearance.conditions.length} {r.clearance.conditions.length === 1 ? 'condition' : 'conditions'}
                        </span>
                      )}
                    </td>
                    <td>
                      {shortDate(r.lastReceivedAt)}
                      {r.receiveCount > 1 && <span className="int-sub">{r.receiveCount} times</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {/* The count at the foot, as on every list; a filtered view says
              which answer it is showing and how to see them all. */}
          <ListCount shown={rows.length} total={total} noun="requests">
            {filter && (
              <>
                , {TILE_WORD[filter].toLowerCase()} only <a href="#/procurement">Show all</a>
              </>
            )}
          </ListCount>
        </div>
      )}

      <RequestDrawer request={selected} open={open && selected != null} onClose={() => setOpen(false)} />

      <Approvals />
      <RegisterChecks />
      {can('config.read') && <Keys />}
    </div>
  )
}

// One request: the answer, each reason with who has to act, what blocks the
// purchase, the conditions that travel with the answer, and where it came from.
function RequestDrawer({ request, open, onClose }: { request: ProcurementRequest | null; open: boolean; onClose: () => void }) {
  const r = request
  const c = r?.clearance
  const blocks = c?.reasons.filter((x) => REASON[x.code]?.blocks) ?? []
  const work = c?.reasons.filter((x) => !REASON[x.code]?.blocks) ?? []

  return (
    <Dialog
      shape="drawer"
      kind="record"
      open={open}
      onClose={onClose}
      title={
        c ? (
          <>
            <span className="int-drawer-name">{c.legalName}</span>
            <ClearanceChip status={c.status} />
          </>
        ) : (
          ''
        )
      }
      primary={r ? { label: 'Open the vendor', href: '#/vendor/' + encodeURIComponent(r.vendorId) } : undefined}
      cancel={false}
    >
      {r && c && (
        <>
          {blocks.length > 0 && (
            <section className="int-section" aria-labelledby="req-blocks">
              <h3 id="req-blocks">What stops the purchase</h3>
              <ul className="int-blocks">
                {blocks.map((b) => (
                  <li key={b.code}>
                    <Status tone="crit">{REASON[b.code]!.short}</Status>
                    <span>{b.words}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {work.length > 0 && (
            <section className="int-section" aria-labelledby="req-why">
              <h3 id="req-why">{c.status === 'CLEARED' ? 'Why it is cleared' : 'What is needed'}</h3>
              <TaskList
                items={work.map((w) => {
                  const known = REASON[w.code]
                  // A known reason carries a short name and, under it, the
                  // sentence the buying system was sent. A task in progress
                  // shows its note as the label, and a code this screen does
                  // not know is shown as its own sentence.
                  return known
                    ? { key: w.code, name: known.short, owner: known.owner, state: known.state ?? 'progress', note: known.note ?? w.words }
                    : { key: w.code, name: w.words, state: 'progress' as TaskState, note: 'Open' }
                })}
              />
            </section>
          )}

          {c.conditions.length > 0 && (
            <section className="int-section" aria-labelledby="req-cond">
              <h3 id="req-cond">Conditions sent with the answer</h3>
              <ul className="int-plain">
                {c.conditions.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="int-section" aria-labelledby="req-facts">
            <h3 id="req-facts">The request</h3>
            <dl className="int-facts">
              <Fact label="Asked by">
                <SourceMark code={r.sourceSystem} />
              </Fact>
              <Fact label="Request">
                <span className="kit-mono">{r.externalRequestId}</span>
              </Fact>
              {r.externalSupplierId && (
                <Fact label="Supplier there">
                  <span className="kit-mono">{r.externalSupplierId}</span>
                </Fact>
              )}
              <Fact label="Supplier here">
                <span className="kit-mono">{c.vendorCode}</span>
                {c.tierLabel ? ', ' + c.tierLabel : ''}
              </Fact>
              <Fact label="Match">{MATCHED_WORD[r.matchedBy] ?? r.matchedBy}</Fact>
              {(r.requesterName || r.requesterEmail) && <Fact label="Requested by">{r.requesterName || r.requesterEmail}</Fact>}
              <Fact label="Asked">
                {r.receiveCount > 1
                  ? r.receiveCount + ' times, ' + shortDate(r.firstReceivedAt) + ' to ' + shortDate(r.lastReceivedAt)
                  : shortDate(r.lastReceivedAt)}
              </Fact>
              <Fact label="Answer decided">{shortDate(c.decidedAt)}</Fact>
              <Fact label="Next review">{c.nextReviewOn ? shortDate(c.nextReviewOn) : 'On change'}</Fact>
            </dl>
          </section>
        </>
      )}
    </Dialog>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

// The approvals a buying system holds for this product: SAP Ariba's supplier
// registrations today, Oracle's spend authorizations once that connector is
// in. Shown only once there are any, because a tenant without them has
// nothing to read here.
function Approvals() {
  const [tasks, setTasks] = useState<ApprovalTask[] | null>(null)
  useEffect(() => {
    api
      .procurementApprovals()
      .then((r) => setTasks(r.approvals))
      .catch(() => setTasks([]))
  }, [])
  if (!tasks || tasks.length === 0) return null
  return (
    <section className="panel">
      <div className="int-title">
        <h2>Approvals answered</h2>
        <Toggletip label="About approvals">
          A cleared supplier is approved, a blocked one denied with the reasons when that is switched on, and one still
          being assessed waits.
        </Toggletip>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Task</th>
              <th scope="col">Supplier</th>
              <th scope="col">Answer</th>
              <th scope="col">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td className="kit-mono">{t.taskId}</td>
                <td>
                  {t.vendorId ? (
                    <a href={'#/vendor/' + encodeURIComponent(t.vendorId)}>{t.externalSupplierId || 'Open the vendor'}</a>
                  ) : (
                    t.externalSupplierId || '-'
                  )}
                </td>
                <td>
                  <span className="int-inline">
                    <Status tone={APPROVAL_WORD[t.state].tone}>{APPROVAL_WORD[t.state].word}</Status>
                    {t.detail && <Toggletip label={'What happened to task ' + t.taskId}>{clip(t.detail)}</Toggletip>}
                  </span>
                </td>
                <td>{shortDate(t.answeredAt ?? t.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// The statutory identity watch: whether it runs, and what it last found. A
// watch that is not configured says so, because silence reads as "all fine".
function RegisterChecks() {
  const [watch, setWatch] = useState<{ configured: boolean; note: string; checks: StatutoryCheck[] } | null>(null)
  useEffect(() => {
    api.statutoryWatch().then(setWatch).catch(() => setWatch(null))
  }, [])
  if (!watch) return null
  const adverse = watch.checks.filter((c) => c.answer === 'PROVEN' && c.status && c.status !== 'ACTIVE')
  return (
    <section className="panel">
      <div className="int-title">
        <h2>GST and company register checks</h2>
        <Status tone={watch.configured ? 'ok' : 'warn'}>{watch.configured ? 'Running' : 'Not configured'}</Status>
        {watch.note && <Toggletip label="About the register checks">{clip(watch.note)}</Toggletip>}
      </div>
      {!watch.configured && can('config.read') && (
        <p className="int-away">
          <a href="#/settings?t=integrations">Set it up in Integrations</a>
        </p>
      )}
      {adverse.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Supplier</th>
                <th scope="col">Register</th>
                <th scope="col">Status there</th>
                <th scope="col">Checked</th>
              </tr>
            </thead>
            <tbody>
              {adverse.slice(0, 20).map((c) => (
                <tr key={c.vendorId + c.identifier + c.checkedAt}>
                  <td>
                    <a href={'#/vendor/' + encodeURIComponent(c.vendorId)}>{c.legalName}</a>
                  </td>
                  <td>
                    {c.kind === 'GSTIN' ? 'GSTIN' : 'Company'} <span className="int-sub kit-mono">{c.identifier}</span>
                  </td>
                  <td>
                    <Status tone="crit">{sentence(c.status ?? '')}</Status>
                    {c.effectiveOn && <span className="int-sub">from {shortDate(c.effectiveOn)}</span>}
                  </td>
                  <td>{shortDate(c.checkedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {watch.configured && adverse.length === 0 && watch.checks.length > 0 && (
        <p className="int-away">
          {watch.checks.length} recent {watch.checks.length === 1 ? 'check' : 'checks'}, none adverse
        </p>
      )}
    </section>
  )
}

function sentence(code: string): string {
  const s = code.toLowerCase().replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// The keys buying systems file requests with. Issued in a dialog, shown once.
function Keys() {
  const [list, setList] = useState<ProcurementCredential[] | null>(null)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'API_KEY' | 'WEBHOOK_HMAC'>('API_KEY')
  const [system, setSystem] = useState('')
  const [intake, setIntake] = useState(true)
  const [lookup, setLookup] = useState(true)
  const [issued, setIssued] = useState<IssuedProcurementCredential | null>(null)
  const [problem, setProblem] = useState('')
  const save = useSave(open, 'Issue a key')
  const busy = save.busy
  const mayWrite = can('config.write')

  const load = useCallback(() => {
    api
      .procurementCredentials()
      // A key issued for the vendor registry alone is listed there, not
      // among the keys buying systems file requests with.
      .then((r) => setList(r.credentials.filter((c) => c.scopes.some((s) => s === 'intake' || s === 'clearance'))))
      .catch((e) => setProblem(describe(e)))
  }, [])
  useEffect(load, [load])

  function issue(e: FormEvent) {
    e.preventDefault()
    setProblem('')
    const scopes = [intake ? 'intake' : '', lookup ? 'clearance' : ''].filter(Boolean)
    save.send(api.createProcurementCredential({ name, kind, scopes, sourceSystem: system || undefined }), {
      // The dialog stays open and turns into the key, because the key is shown
      // once and the person who asked for it is looking at this dialog.
      shown: setIssued,
      done: () => {
        setName('')
        setSystem('')
        load()
      },
      failed: (err) => setProblem(describe(err)),
      lost: 'The key was issued, but its dialog was closed before the key could be shown, and it cannot be shown again. Revoke it and issue another.',
    })
  }


  function close() {
    setOpen(false)
    setIssued(null)
    setProblem('')
  }

  return (
    <section className="panel">
      <div className="p1-head">
        <div>
          <div className="int-title">
            <h2>Keys for buying systems</h2>
            <Toggletip label="About keys">
              A buying system with no connector here files purchase requests and asks for a clearance with one of these.
              Each key can file, look up, or both, and nothing else.
            </Toggletip>
          </div>
          <p className="muted">For a buying system with no connector here</p>
        </div>
        <div className="p1-actions">
          <button
            type="button"
            className="button primary"
            disabled={!mayWrite}
            title={mayWrite ? undefined : refusalReason('config.write')}
            onClick={() => setOpen(true)}
          >
            Issue a key
          </button>
        </div>
      </div>

      {problem && !open && <p className="note warn">{problem}</p>}

      <Dialog
        shape="modal"
        kind="form"
        open={open}
        onClose={close}
        title={issued ? 'Copy this key now' : 'Issue a key'}
        busy={busy}
        // Once the key is on screen the dialog asks before it closes, and the
        // button that says it was copied is on the other side from the one that
        // asked for it, so a second press of the same spot cannot dismiss a key
        // nobody has read.
        guard={issued ? 'Close without copying the key? It cannot be shown again.' : undefined}
        more={
          issued ? (
            <button type="button" onClick={close}>
              I have copied it
            </button>
          ) : undefined
        }
        primary={
          issued
            ? undefined
            : {
                label: busy ? 'Issuing' : 'Issue and show it once',
                form: 'proc-key-form',
                off: !intake && !lookup && 'Tick at least one thing the key may do',
                actionId: 'tprm.procurement.a01',
              }
        }
      >
        {issued ? (
          <div className="int-section" role="status">
            <p className="int-helper">It cannot be shown again</p>
            <p className="mono int-wrap int-secret">
              {issued.apiKey ? issued.apiKey : 'Key id ' + issued.keyId + ', signing secret ' + issued.signingSecret}
            </p>
            <p className="int-prose">{issued.how}</p>
          </div>
        ) : (
          <form id="proc-key-form" className="wizard-form int-form" onSubmit={issue}>
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Coupa purchase requests" required />
            </label>
            <label>
              How it proves itself
              <select value={kind} onChange={(e) => setKind(e.target.value as 'API_KEY' | 'WEBHOOK_HMAC')}>
                <option value="API_KEY">It sends the key</option>
                <option value="WEBHOOK_HMAC">It signs each call</option>
              </select>
            </label>
            <label>
              Only for this buying system
              <input value={system} onChange={(e) => setSystem(e.target.value)} placeholder="COUPA" />
              <span className="field-note">Leave empty to let it speak for any. Set, a request naming another system is refused.</span>
            </label>
            <fieldset className="fieldset int-fieldset">
              <legend>It may</legend>
              <label className="check">
                <input type="checkbox" checked={intake} onChange={(e) => setIntake(e.target.checked)} /> File purchase requests
              </label>
              <label className="check">
                <input type="checkbox" checked={lookup} onChange={(e) => setLookup(e.target.checked)} /> Look up a clearance
              </label>
            </fieldset>
            {problem && <p className="note warn">{problem}</p>}
          </form>
        )}
      </Dialog>

      {list && list.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">May</th>
                <th scope="col">Last used</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <span className="int-sub">
                      {c.kind === 'API_KEY' ? 'Sends the key' : 'Signs each call'}
                      {c.sourceSystem ? ', ' + buyingSystem(c.sourceSystem).name + ' only' : ''}
                    </span>
                  </td>
                  <td>{scopesSentence(c.scopes)}</td>
                  <td>{c.revokedAt ? <Status tone="warn">Revoked</Status> : c.lastUsedAt ? shortDate(c.lastUsedAt) : 'Not yet'}</td>
                  <td>
                    {!c.revokedAt && mayWrite && (
                      <button
                        type="button"
                        className="button ghost"
                        data-action="tprm.procurement.a02"
                        onClick={() =>
                          void api
                            .revokeProcurementCredential(c.id)
                            .then(load)
                            .catch((e) => setProblem(describe(e)))
                        }
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
